import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../api-base-url.token';

/**
 * Langues patient supportées — miroir de SupportedLanguages.java (waitora-shared).
 * Toute langue ajoutée ici doit d'abord exister côté backend, sinon elle sera
 * filtrée silencieusement à l'enregistrement (EstablishmentsController/EstablishmentService).
 */
/** Labels sans emoji drapeau : l'affichage utilise des icônes SVG (voir flagIcon(), core/utils/flag-icon.util.ts) —
 *  les drapeaux emoji ne rendent pas correctement sur Windows (Segoe UI Emoji affiche "FR"/"SA" en texte brut). */
export const PATIENT_LANGUAGES: { code: string; label: string }[] = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
];

/* ============================================================
 * DTOs alignés avec EstablishmentsController (backend)
 * ============================================================ */
export interface SlotDto { start: string; end: string; }
export interface DayDto { weekday: number; slots: SlotDto[]; }

export type QueueStatus = 'OPEN' | 'CLOSED' | string | null;

export interface QueueRowDto {
  id: string;
  name: string;
  name_i18n?: string | null;
  status: QueueStatus;
  patient_message: string | null;
  patient_message_i18n?: string | null;
  public_slug: string | null;
}

export interface LinksDto { _public: string; widget: string; }

export interface NearbyEstablishmentDto {
  id: string;
  name: string;
  name_i18n?: string | null;
  address?: string | null;
}

export interface NearbyConfigDto {
  selected: NearbyEstablishmentDto[];
  candidates: NearbyEstablishmentDto[];
}

export interface EstablishmentDetailsDto {
  id: string;
  org_id: string;
  name: string;
  tz: string;
  address: string | null;
  place_id: string | null;
  website: string | null;
  langues: string[];
  opening_hours: DayDto[];
  links: LinksDto;
  queues: QueueRowDto[];
  show_patient_reason?: boolean;
  show_end_prestation?: boolean;
  widget_enabled?: boolean;
  show_forecast?: boolean;
  show_patient_reply?: boolean;
  show_feeling_section?: boolean;
  show_google_review?: boolean;
  quiz_enabled?: boolean;
  prevention_point_enabled?: boolean;
  prevention_point_start_on_launch?: boolean;
  prescripteur_visible?: boolean;
  show_understand_care?: boolean;
  /** Accueil patient : afficher le temps d'attente (faux = accueil seulement ; TV, widget, API inchangés). */
  show_wait_time?: boolean;
  has_photo?: boolean;
  photo_updated_at?: string | null;
}

/* ============================================================
 * Payloads
 * ============================================================ */
export interface UpdateInfoPayload {
  name?: string;
  langues?: string[];
  tz?: string;
  address?: string | null;
  place_id?: string | null;
  website?: string | null;
  show_patient_reason?: boolean;
  show_end_prestation?: boolean;
  widget_enabled?: boolean;
  show_forecast?: boolean;
  show_patient_reply?: boolean;
  show_feeling_section?: boolean;
  show_google_review?: boolean;
  quiz_enabled?: boolean;
  prevention_point_enabled?: boolean;
  prevention_point_start_on_launch?: boolean;
  prescripteur_visible?: boolean;
  show_understand_care?: boolean;
  /** Accueil patient : afficher le temps d'attente (faux = accueil seulement ; TV, widget, API inchangés). */
  show_wait_time?: boolean;
}
export interface ReplaceHoursPayload {
  opening_hours: DayDto[];
}
export interface UpdateQueuePayload {
  name?: string;
  name_i18n?: string | null;
  patient_message?: string | null;
  patient_message_i18n?: string | null;
}
export interface CreateEstablishmentPayload {
  name: string;
  address?: string | null;
  tz: string;
  langues?: string[];
}
export interface CreateQueuePayload {
  name: string;
  name_i18n?: string | null;
  patient_message?: string | null;
  patient_message_i18n?: string | null;
}

/* ============================================================
 * Service HTTP
 * ============================================================ */
@Injectable({ providedIn: 'root' })
export class EstablishmentsService {
  private http = inject(HttpClient);

  // supporte '', '/', 'https://api', 'https://api/'
  private base = inject(API_BASE_URL).replace(/\/+$/, '');

  private join(path: string): string {
    if (!this.base) return path; // même origine (dev proxy)
    return `${this.base}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  /** GET détails d’un établissement */
  getDetails(id: string): Observable<EstablishmentDetailsDto> {
    return this.http.get<EstablishmentDetailsDto>(
      this.join(`/api/v1/establishments/${id}`)
    );
  }

  /** PUT infos de base (name, tz, address) */
  updateInfo(id: string, payload: UpdateInfoPayload): Observable<void> {
    return this.http.put<void>(
      this.join(`/api/v1/establishments/${id}`),
      payload
    );
  }

  /** PUT remplacement intégral des horaires */
  replaceOpeningHours(id: string, payload: ReplaceHoursPayload): Observable<void> {
    return this.http.put<void>(
      this.join(`/api/v1/establishments/${id}/opening-hours`),
      payload
    );
  }

  /** PUT édition d’une file (nom + message patient) */
  updateQueue(queueId: string, payload: UpdateQueuePayload): Observable<void> {
    return this.http.put<void>(
      this.join(`/api/v1/establishments/queues/${queueId}`),
      payload
    );
  }

  /** DELETE suppression d’une file */
  deleteQueue(queueId: string): Observable<void> {
    return this.http.delete<void>(
      this.join(`/api/v1/establishments/queues/${queueId}`)
    );
  }

  /** POST création d’un établissement (retourne l’id créé) */
  createEstablishment(payload: CreateEstablishmentPayload): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(
      this.join(`/api/v1/establishments`),
      payload
    );
  }

  /** POST création d’une file pour un établissement */
  createQueue(estabId: string, payload: CreateQueuePayload): Observable<{ id: string; public_slug?: string }> {
    return this.http.post<{ id: string; public_slug?: string }>(
      this.join(`/api/v1/establishments/${estabId}/queues`),
      payload
    );
  }

  /** NEW: suppression d’un établissement */
  deleteEstablishment(id: string): Observable<void> {
    return this.http.delete<void>(
      this.join(`/api/v1/establishments/${id}`)
    );
  }

  /** POST upload de la photo propre à cet établissement (indépendante du logo d'organisation, max 10 Mo) */
  uploadPhoto(id: string, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<void>(this.join(`/api/v1/establishments/${id}/photo`), formData);
  }

  /** DELETE suppression de la photo de l'établissement */
  deletePhoto(id: string): Observable<void> {
    return this.http.delete<void>(this.join(`/api/v1/establishments/${id}/photo`));
  }

  /** URL publique de la photo (pas d'auth requise), avec cache-busting sur photo_updated_at */
  getPhotoUrl(id: string, photoUpdatedAt?: string | null): string {
    const url = this.join(`/public/v1/establishments/${id}/photo`);
    return photoUpdatedAt ? `${url}?v=${encodeURIComponent(photoUpdatedAt)}` : url;
  }

  /** GET labos "à proximité" configurés pour le widget + candidats (même organisation) */
  getNearby(id: string): Observable<NearbyConfigDto> {
    return this.http.get<NearbyConfigDto>(
      this.join(`/api/v1/establishments/${id}/nearby`)
    );
  }

  /** POST ajout d’un établissement à la liste "à proximité" */
  addNearby(id: string, nearbyEstablishmentId: string): Observable<void> {
    return this.http.post<void>(
      this.join(`/api/v1/establishments/${id}/nearby`),
      { nearbyEstablishmentId }
    );
  }

  /** DELETE retrait d’un établissement de la liste "à proximité" */
  removeNearby(id: string, nearbyEstablishmentId: string): Observable<void> {
    return this.http.delete<void>(
      this.join(`/api/v1/establishments/${id}/nearby/${nearbyEstablishmentId}`)
    );
  }
}
